/**
 * The vidIQ pipeline (SIL-10670) — is it writing, and how far has it got?
 *
 * WHY STALENESS IS THE FIRST THING THIS PANEL SAYS.
 * The pipeline this replaced ran for three months writing nothing. Its last row
 * landed 2026-04-21; three containers stayed up and reported "healthy"
 * throughout, because its scraper's CSS selectors had silently stopped matching
 * and a page that parsed to zero rows was recorded as a successful scrape. Row
 * counts, container health and queue depth all looked normal the entire time.
 *
 * The one number that would have caught it on day one is "how long since a row
 * was actually written". So it is the headline here, it is loud past a
 * threshold, and it is never averaged into anything.
 *
 * COVERAGE IS AGAINST THE SCOPED SET, not the whole table. The scope is a
 * deliberate deployment choice (VIDIQ_MIN_SUBSCRIBERS bounds the run to the
 * highest-subscriber channels), so measuring against all 3.6M would render an
 * on-target run as 1.4% complete and train an operator to ignore the bar.
 */
import { useVidiqCounts, useThroughput, useVidiqTiers } from '../api/queries'
import { PanelState, Stat, Tile } from '../components/ui'
import { n, compact, pct, age } from '../components/format'

const STALE_S = 7200

/** How long "nothing queued" may explain silence. Channels age past the refresh
 *  interval every day, so a lane quiet for longer than this is not just caught up. */
const IDLE_LIMIT_S = 172_800

export type VidiqLane = 'writing' | 'idle' | 'stalled' | 'unknown'

/** What the staleness MEANS. Silence with work queued, or with no feeder alive, is
 *  the outage this tab exists to catch. Silence with an empty queue and a live
 *  feeder is a lane that has caught up: the 6h "CRISIS" of 2026-11-01 was every
 *  page-servable channel fresh and 4,846 API-only ones held back on purpose. */
export function vidiqLane(v: {
  stale_s: number | null
  queued?: number | null | undefined
  feeder_alive?: boolean | null | undefined
} | undefined): VidiqLane {
  if (!v || v.stale_s === null) return 'unknown'
  if (v.stale_s <= STALE_S) return 'writing'
  return v.queued === 0 && v.feeder_alive === true && v.stale_s < IDLE_LIMIT_S
    ? 'idle' : 'stalled'
}

/** Past this, the pipeline is not merely quiet — it has stopped. Two hours is
 *  several multiples of a healthy cycle's gap between writes, and small enough
 *  that a three-month outage could never hide behind it again. */
function ago(seconds: number | null): string {
  if (seconds === null) return 'never'
  // NEGATIVE MEANS THE CLOCKS DISAGREE, not that a write is in the future. The
  // figure is server_now - last_write across two machines, so a client ahead of
  // the server yields a small negative; rendering "-30s ago" reads as a bug in
  // the pipeline when it is a bug in NTP. Clamp to now: the honest reading is
  // "just written", which is what a negative delta actually means.
  if (seconds < 0) seconds = 0
  if (seconds < 90) return `${seconds}s ago`
  // MINUTES UP TO THE ALARM THRESHOLD, hours only past it. The obvious cutoff
  // (90 min) made a HEALTHY sample and an ALARMING one render identically:
  // 5,400 s rounds to "2h ago" and so does 7,200 s, so a pipeline 90 minutes
  // quiet looked exactly like one that had crossed the line. The headline has
  // to agree with the verdict beside it, so the unit changes where the verdict
  // does.
  if (seconds < STALE_S) return `${Math.round(seconds / 60)}m ago`
  if (seconds < 172800) return `${Math.round(seconds / 3600)}h ago`
  return `${Math.round(seconds / 86400)}d ago`
}

export function Vidiq() {
  const { data, isLoading, error } = useVidiqCounts()
  const lane = vidiqLane(data)
  const stalled = lane === 'stalled'

  return (
    <Tile
      title="vidIQ"
      hint="channel statistics — daily series"
    >
      <PanelState isLoading={isLoading} error={error}>
        <div className="stats-row">
          <Stat
            label="last write"
            value={ago(data?.stale_s ?? null)}
            state={stalled ? 'crit' : lane === 'idle' ? 'warn' : 'ok'}
            sub={data?.last_written ?? 'never written'}
          />
          {/* Cumulative, and reported by the writer. See the schema for why
              this is not a Postgres count. */}
          <Stat label="rows written" value={n(data?.rows_written ?? null)} />
          <Stat
            label="scope"
            value={data?.scope_min_subscribers
              ? `≥ ${n(data.scope_min_subscribers)}`
              : 'all'}
            sub="subscribers"
          />
        </div>

        {stalled ? (
          <p className="panel-err" style={{ marginTop: 10 }}>
            Last row written {ago(data!.stale_s)}. This is the exact shape of the
            outage this pipeline replaced — check the vidiq worker before
            trusting any figure above.
          </p>
        ) : null}
        {lane === 'idle' ? (
          <p className="note" style={{ marginTop: 10 }}>
            Nothing queued, and the feeder is alive: every channel the page can
            serve is fresh.
            {data?.api_resting
              ? ' Channels only the API can serve are held back while the vidIQ API rests.'
              : ''}
          </p>
        ) : null}

      </PanelState>
    </Tile>
  )
}

/** How far behind a tier's newest data point may fall before it is called out.
 *
 *  vidIQ serves a rolling 30-day daily window, so a tier whose newest row is
 *  older than that has passed the point where the next fetch's window still
 *  OVERLAPS what we hold — every day beyond it is permanently unrecoverable,
 *  from any source, at any effort. 7 and 30 are therefore not arbitrary bands:
 *  7 is "drifting, still fully recoverable", 30 is "losing history now". */
const TIER_WARN_DAYS = 7
const TIER_CRIT_DAYS = 30

/** Whole days between a YYYY-MM-DD data point and today, or null when absent. */
function daysBehind(iso: string | null): number | null {
  if (!iso) return null
  const then = Date.parse(iso + 'T00:00:00Z')
  if (Number.isNaN(then)) return null
  // Floor, not round: 1.9 days behind is one whole day of missing data, not two.
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000))
}

function lagState(days: number | null): 'ok' | 'warn' | 'crit' {
  if (days === null) return 'warn'
  if (days >= TIER_CRIT_DAYS) return 'crit'
  if (days >= TIER_WARN_DAYS) return 'warn'
  return 'ok'
}

/**
 * Per-subscriber-tier freshness — where the data actually is, tier by tier.
 *
 * WHY THIS IS SPLIT BY TIER AND NOT REPORTED AS ONE NUMBER. The pipeline is
 * scoped by subscriber count (VIDIQ_MIN_SUBSCRIBERS), and a whole-table average
 * hides precisely the failure that matters: the biggest channels are the fewest
 * rows, so a tier of 3,000 10M+ channels going a week stale moves an
 * all-channel average by nothing at all while being the most valuable inventory
 * we hold. Measured on prod 2026-08-06, that was real — the 10M+ tier's last
 * scrape was over a day old while every other tier was minutes old.
 *
 * TWO DIFFERENT AGES ARE SHOWN ON PURPOSE, and conflating them is the mistake
 * this panel exists to avoid:
 *   last data point  what vidIQ's numbers actually run to
 *   last scraped     when we last successfully wrote for that tier
 * Scraping today can legitimately return data ending days ago for a dormant
 * channel. "Still fetching" and "data is current" are different questions, and
 * only asking the first is how a dead pipeline reads as healthy.
 */
export function VidiqTiers() {
  const { data, isLoading, error, refetch, isFetching } = useVidiqTiers()
  const rows = data?.tiers ?? []

  return (
    <Tile
      title="vidIQ by subscriber tier"
      hint={
        data?.captured_at
          ? `snapshot ${age(data.age_s)} old${
              data.duration_ms ? ` · scan ${Math.round(data.duration_ms / 1000)}s` : ''}`
          : 'materialised every 6h by the coverage role'
      }
      span
      action={
        <button className="btn" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'reading…' : 'refresh'}
        </button>
      }
    >
      <PanelState isLoading={isLoading} error={error}>
        {/* A SNAPSHOT THAT HAS NOT RUN IS NOT AN EMPTY PIPELINE. Rendering an
            empty table here would read as "no vidIQ data at all", which is the
            single most alarming thing this board can say — and it would be
            wrong. Say what is actually true: nobody has computed it yet. */}
        {!data?.captured_at ? (
          <p className="q">
            Not computed yet. The coverage role captures this every 6h; the
            first capture appears after its next run.
          </p>
        ) : (
          <>
            {/* The snapshot's own age, stated before any figure derived from
                it. A stale snapshot renders identically to a fresh one unless
                it says so. */}
            {data.stale ? (
              <p className="panel-err" style={{ marginBottom: 10 }}>
                Snapshot is {age(data.age_s)} old — older than two capture
                cycles. The coverage role may not be running; every figure below
                is from that capture, not from now.
              </p>
            ) : null}

            <table className="t">
              <thead>
                <tr>
                  <th>tier</th>
                  <th className="n">channels</th>
                  <th className="n">current</th>
                  <th className="n">last data point</th>
                  <th className="n">last scraped</th>
                  <th className="n">subscribers</th>
                  <th className="n">views</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const behind = daysBehind(t.newest_stats_date)
                  const state = lagState(behind)
                  // Share of the tier still advancing. The gap between this and
                  // 100% is the tail that has quietly stopped moving — visible
                  // here and nowhere else on the board.
                  const cur = t.channels
                    ? t.channels_current / t.channels
                    : null
                  return (
                    <tr key={t.tier}>
                      <td>{t.tier}</td>
                      <td className="n">{n(t.channels)}</td>
                      <td className="n">
                        <span
                          title={`${n(t.channels_current)} of ${n(t.channels)} within a few days of this tier's newest row`}
                          style={cur !== null && cur < 0.9
                            ? { color: 'var(--warning)' } : undefined}
                        >
                          {cur === null ? '—' : pct(cur * 100, 0)}
                        </span>
                      </td>
                      <td className="n">
                        <span
                          style={state === 'crit' ? { color: 'var(--crisis)' }
                            : state === 'warn' ? { color: 'var(--warning)' }
                            : undefined}
                          title={
                            behind !== null && behind >= TIER_CRIT_DAYS
                              ? `${behind}d behind — past vidIQ's 30-day window, so those days cannot be refetched from any source`
                              : `${behind ?? '?'}d behind today`
                          }
                        >
                          {t.newest_stats_date ?? '—'}
                          {behind !== null && behind > 0
                            ? <span className="q"> ·{behind}d</span> : null}
                        </span>
                      </td>
                      <td className="n q">
                        {t.last_scraped_at
                          ? t.last_scraped_at.slice(0, 16).replace('T', ' ')
                          : '—'}
                      </td>
                      <td className="n q">{compact(t.subscribers)}</td>
                      <td className="n q">{compact(t.total_views)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* The rule behind the colours, stated once. A threshold nobody can
                see the reasoning for is a threshold operators learn to ignore. */}
            <p className="q" style={{ marginTop: 8 }}>
              vidIQ serves a rolling 30-day window. A tier more than{' '}
              {TIER_CRIT_DAYS}d behind has passed the point where the next
              fetch still overlaps what we hold — those days are unrecoverable.
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}

/** The two vidIQ roles, split out of the collector throughput table so the
 *  pipeline reads as its own thing rather than two extra rows in somebody
 *  else's list. Both write `metrics:completed:{role}`; the FEEDER is shown
 *  because a feeder that stops sweeping and a worker that stops fetching look
 *  identical downstream (an empty queue) and only the per-role rate separates
 *  them. */
const VIDIQ_ROLES: Array<[string, string]> = [
  ['vidiq', 'vidiq worker'],
  ['vidiq-feeder', 'vidiq feeder'],
]

export function VidiqThroughput() {
  const { data, isLoading, error, refetch, isFetching } = useThroughput()
  return (
    <Tile
      title="vidIQ throughput"
      hint="completed work per second"
      action={
        <button className="btn" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'reading…' : 'refresh'}
        </button>
      }
    >
      <PanelState isLoading={isLoading} error={error}>
        <table className="t">
          <thead>
            <tr>
              <th>processor</th>
              <th className="n">rate</th>
              <th className="n">per min</th>
              <th className="n">completed</th>
            </tr>
          </thead>
          <tbody>
            {VIDIQ_ROLES.map(([role, label]) => {
              const row = data?.roles?.[role]
              // NULL IS NOT ZERO -- the same rule the collector table follows.
              // Null is "no baseline yet" (ordinary, happens after every
              // deploy); zero is "nothing moved", which is actionable.
              const unknown = !row || row.per_s === null
              const stalled = !unknown && row!.per_s === 0 && (row!.total ?? 0) > 0
              return (
                <tr key={role}>
                  <td>{label}</td>
                  <td className="n">
                    {unknown ? (
                      <span className="q" title="press refresh again to measure a rate">—</span>
                    ) : (
                      <span style={stalled ? { color: 'var(--crisis)' } : undefined}>
                        {row!.per_s!.toFixed(2)}<span className="q">/s</span>
                      </span>
                    )}
                  </td>
                  <td className="n q">
                    {row?.per_min === null || row?.per_min === undefined
                      ? '—' : n(Math.round(row.per_min))}
                  </td>
                  <td className="n q">{n(row?.total ?? null)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </PanelState>
    </Tile>
  )
}
